import { useState } from 'react';
import { Check, TriangleAlert, Info } from 'lucide-react';
import { useActions, useRegimen, useAgentParams, useDoseRounding, useMIC, useStrategy, usePKParameters } from '@/store/selectors';
import { ConfidenceStatus, IntervalRecommendation, PKSource, Strategy } from '@/types';
import { Collapsible } from '@/components/shared/Collapsible';
import { OverrideReasonModal } from '@/components/modals/OverrideReasonModal';
import { ExpressionInput, Select } from 'calc-controls';

const CONFIDENCE_LABEL: Record<ConfidenceStatus, string> = {
  [ConfidenceStatus.Empiric]: 'Empiric',
  [ConfidenceStatus.LevelInformed]: 'Level-Informed',
  [ConfidenceStatus.OutOfRange]: 'Out of Range',
  [ConfidenceStatus.RequiresReview]: 'Requires Review',
};

const CONFIDENCE_CLASS: Record<ConfidenceStatus, string> = {
  [ConfidenceStatus.Empiric]: 'badge badge-info',
  [ConfidenceStatus.LevelInformed]: 'badge badge-safe',
  [ConfidenceStatus.OutOfRange]: 'badge badge-warn',
  [ConfidenceStatus.RequiresReview]: 'badge badge-warn',
};

// Non-color severity cue (colorblind-safety): an icon redundantly encodes the
// confidence level so the safe/warn distinction survives a deuteranopia view.
const CONFIDENCE_ICON: Record<ConfidenceStatus, JSX.Element> = {
  [ConfidenceStatus.Empiric]: <Info size={12} strokeWidth={2} aria-hidden="true" />,
  [ConfidenceStatus.LevelInformed]: <Check size={12} strokeWidth={2.5} aria-hidden="true" />,
  [ConfidenceStatus.OutOfRange]: <TriangleAlert size={12} strokeWidth={2} aria-hidden="true" />,
  [ConfidenceStatus.RequiresReview]: <TriangleAlert size={12} strokeWidth={2} aria-hidden="true" />,
};

const INTERVAL_LABEL: Record<IntervalRecommendation, string> = {
  [IntervalRecommendation.Q24h]: 'q24h',
  [IntervalRecommendation.Q36h]: 'q36h',
  [IntervalRecommendation.Q48h]: 'q48h',
  [IntervalRecommendation.Q12h]: 'q12h',
  [IntervalRecommendation.Hold]: 'HOLD',
  [IntervalRecommendation.ExitExtendedInterval]: 'EXIT — use traditional',
};

/** Regimen output: dose, interval, predicted levels, rationale, rounding, override (TODO Task 1.25). */
export function RegimenResults(): JSX.Element {
  const regimen = useRegimen();
  const actions = useActions();
  const agentParams = useAgentParams();
  const doseRoundingEnabled = useDoseRounding();
  const { micValue } = useMIC();
  const strategy = useStrategy();
  const pkParameters = usePKParameters();
  const [showOverride, setShowOverride] = useState(false);
  const [overrideDose, setOverrideDose] = useState('');
  const [overrideInterval, setOverrideInterval] = useState('');
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [micInput, setMicInput] = useState('');
  const [micSrc, setMicSrc] = useState('assumed');
  const [micFeedback, setMicFeedback] = useState<'applied' | 'cleared' | null>(null);
  const [confirmClearMic, setConfirmClearMic] = useState(false);

  const triggerMicFeedback = (kind: 'applied' | 'cleared'): void => {
    setMicFeedback(kind);
    window.setTimeout(() => setMicFeedback(null), 1800);
  };

  if (!regimen) {
    return (
      <section className="panel" aria-label="Regimen results" data-testid="regimen-results">
        <h2 className="panel-title">Regimen</h2>
        <div className="empty-state">Enter patient details and select a drug to see the recommended regimen, or use <strong>Load Demo</strong> (top right) for a worked example.</div>
      </section>
    );
  }

  // E-DF-1: prefer strategy-specific maintenance dose (Traditional/Synergy) over the mg/kg-based
  // empiric value, so the displayed dose matches what the engine actually computed.
  const doseMg = regimen.maintenanceDoseMg ?? regimen.roundedDoseMg ?? regimen.empiricDoseMg;
  // E-DF-1: prefer traditionalIntervalHr (real interval for Traditional/Synergy); empiric is 0
  // for non-EI strategies and would render "q0h". EI path uses INTERVAL_LABEL[eiInterval] below.
  const intervalHr = regimen.traditionalIntervalHr ?? regimen.empiricIntervalHr;
  const peak = regimen.roundedPredictedPeak ?? regimen.predictedPeakMg;
  const trough = regimen.roundedPredictedTrough ?? regimen.predictedTroughMg;

  const submitOverride = (reason: string): void => {
    actions.overrideRegimen(Number(overrideDose), Number(overrideInterval), reason);
    setShowReasonModal(false);
    setShowOverride(false);
    setOverrideDose('');
    setOverrideInterval('');
  };

  return (
    <section className="panel regimen-summary animate-enter-up" aria-label="Regimen results" data-testid="regimen-results">
      <h2 className="panel-title panel-title-lg">Recommended Regimen</h2>
      <div className="badges-row">
        <span className={CONFIDENCE_CLASS[regimen.confidence]} data-testid="confidence-badge" title="Empiric: no drug levels yet. Level-Informed: computed from measured levels. Out of Range / Requires Review: clinical verification needed.">
          {CONFIDENCE_ICON[regimen.confidence]}
          {CONFIDENCE_LABEL[regimen.confidence]}
        </span>
        <span className="badge badge-info" data-testid="pk-source-indicator">
          PK: {pkParameters ? 'Patient-Specific' : strategy.pkSource === PKSource.PatientSpecific ? 'Patient-Specific' : 'Population'}
        </span>
      </div>
      <div className="regimen-dose-label">Recommended Dose</div>
      <div className="regimen-dose-block">
        <span className="regimen-dose-value">{doseMg.toFixed(0)}</span>
        <span className="regimen-dose-unit">mg</span>
        <span className="regimen-interval">
          {regimen.strategy === Strategy.ExtendedInterval && regimen.eiInterval
            ? INTERVAL_LABEL[regimen.eiInterval]
            : `q${intervalHr}h`}
        </span>
      </div>
      {/* E-DF-1: Traditional strategy shows a loading dose alongside maintenance + interval (Task 1.25). */}
      {regimen.loadingDoseMg != null && (
        <div className="value-row">
          <span className="value-label">Loading dose</span>
          <span className="value-num">{regimen.loadingDoseMg.toFixed(0)} mg</span>
        </div>
      )}
      {regimen.eiIntervalReason && (
        <div className="hint-text">{regimen.eiIntervalReason}</div>
      )}

      {(peak != null || trough != null) && (
        <div className="field-row mt-3">
          {peak != null && (
            <div className="value-row">
              <span className="value-label">Predicted peak</span>
              <span className={`value-num ${regimen.peakMeetsTarget ? 'text-ok' : 'text-warn'}`}>
                {regimen.peakMeetsTarget
                  ? <Check size={14} strokeWidth={2.5} aria-hidden="true" className="icon-inline" />
                  : <TriangleAlert size={14} strokeWidth={2} aria-hidden="true" className="icon-inline" />}
                {peak.toFixed(1)} <span className="unit">mg/L</span>
              </span>
            </div>
          )}
          {trough != null && (
            <div className="value-row">
              <span className="value-label">Predicted trough</span>
              <span className={`value-num ${regimen.troughMeetsTarget ? 'text-ok' : 'text-warn'}`}>
                {regimen.troughMeetsTarget
                  ? <Check size={14} strokeWidth={2.5} aria-hidden="true" className="icon-inline" />
                  : <TriangleAlert size={14} strokeWidth={2} aria-hidden="true" className="icon-inline" />}
                {trough.toFixed(1)} <span className="unit">mg/L</span>
              </span>
            </div>
          )}
        </div>
      )}
      {(peak != null || trough != null) && (
        <p className="hint-text">Steady-state predictions from one-compartment intermittent infusion model. Check icon = within agent-specific target range; alert icon = outside target.</p>
      )}

      {regimen.auc24 != null && (
        <div className="value-row" title="Area under the concentration-time curve over 24 hours.">
          <span className="value-label">AUC₂₄</span>
          <span className="value-num">
            {regimen.auc24.toFixed(1)} mg·h/L
          </span>
        </div>
      )}
      {regimen.aucMic != null && regimen.mic != null && (
        <div className="value-row" title="AUC₂₄ divided by MIC. PK/PD bactericidal exposure index.">
          <span className="value-label">AUC/MIC (MIC {regimen.mic} mg/L)</span>
          <span className="value-num">{regimen.aucMic.toFixed(0)}</span>
        </div>
      )}

      {regimen.roundedDoseMg != null && regimen.roundedDoseMg !== regimen.empiricDoseMg && (
        <div className="hint-text">
          Rounded to {regimen.roundedDoseMg.toFixed(0)} mg (increment {agentParams.roundingIncrementMg} mg).
        </div>
      )}

      {regimen.roundedDoseCappedAtMax && (
        <div className="badge badge-warn" data-testid="dose-capped-alert">
          <TriangleAlert size={12} strokeWidth={2} aria-hidden="true" />
          Dose capped at agent max — ideal {regimen.roundedIdealDoseMg?.toFixed(0)} mg; consider confirming peak.
        </div>
      )}
      {regimen.roundedDoseFlooredToMin && (
        <div className="badge badge-warn" data-testid="dose-floored-alert">
          <TriangleAlert size={12} strokeWidth={2} aria-hidden="true" />
          Dose floored to agent min.
        </div>
      )}

      <div className="field-row mt-2">
        <div className="field">
          <label htmlFor="dose-round-toggle" title="Rounds the computed dose to the nearest practical increment and recomputes predicted levels." className="checkbox-label">
            <input
              id="dose-round-toggle"
              type="checkbox"
              checked={doseRoundingEnabled}
              onChange={(e) => actions.applyDoseRounding(e.target.checked)}
              data-testid="toggle-dose-rounding"
            />
            Round dose to nearest {agentParams.roundingIncrementMg} mg
          </label>
        </div>
      </div>

      <Collapsible title="PK/PD — MIC input" defaultOpen={false}>
        <div className="field-row">
          <div className="field" title="Minimum inhibitory concentration of the pathogen (mg/L). Required for AUC/MIC and Cmax/MIC ratios.">
            <ExpressionInput
              id="mic-input"
              label="MIC"
              unit="mg/L"
              value={Number(micInput) || 0}
              onChange={(v) => setMicInput(String(v))}
              min={0}
              decimals={1}
              inline
            />
          </div>
          <div className="field">
            <Select
              label="MIC source"
              options={[
                { value: 'assumed', label: 'Assumed' },
                { value: 'lab', label: 'Lab reported' },
                { value: 'eucast', label: 'EUCAST/CLSI breakpoint' },
              ]}
              value={micSrc}
              onChange={setMicSrc}
            />
          </div>
        </div>
        <button
          className="btn btn-secondary btn-sm"
          disabled={!micInput}
          onClick={() => { actions.setMIC(Number(micInput) || null, micSrc); triggerMicFeedback('applied'); }}
          data-testid="apply-mic"
        >
          Apply MIC
        </button>
        {(micValue ?? regimen.mic) != null && (
          <button
            className={`btn btn-sm ${confirmClearMic ? 'btn-danger' : 'btn-secondary'}`}
            onClick={() => {
              if (confirmClearMic) {
                actions.setMIC(null, 'assumed');
                setMicInput('');
                triggerMicFeedback('cleared');
                setConfirmClearMic(false);
              } else {
                setConfirmClearMic(true);
              }
            }}
            onBlur={() => setConfirmClearMic(false)}
            data-testid="clear-mic"
          >
            {confirmClearMic ? 'Confirm clear?' : 'Clear MIC'}
          </button>
        )}
        {micFeedback && (
          <div className="inline-confirm" role="status" aria-live="polite">
            {micFeedback === 'applied' ? 'MIC applied — PK/PD ratios updated.' : 'MIC cleared.'}
          </div>
        )}
        {regimen.cmaxMic != null && (
          <div className="value-row mt-2" title="Peak concentration divided by MIC. Target ≥ 8–10 for Gram-negative bactericidal activity.">
            <span className="value-label">Cmax/MIC</span>
            <span className={`value-num ${regimen.cmaxMic >= 8 ? 'text-ok' : 'text-warn'}`}>
              {regimen.cmaxMic.toFixed(1)}
            </span>
          </div>
        )}
      </Collapsible>

      {regimen.overrideApplied && (
        <div className="badge badge-warn">{<TriangleAlert size={12} strokeWidth={2} aria-hidden="true" />}Override: {regimen.overrideReason}</div>
      )}

      <Collapsible title="Rationale" defaultOpen={false}>
        <p className="rationale-text">{regimen.rationaleText}</p>
      </Collapsible>

      <div className="actions-row">
        <button className="btn btn-secondary btn-sm" onClick={() => setShowOverride(!showOverride)} data-testid="toggle-override">
          {regimen.overrideApplied ? 'Edit Override' : 'Override Regimen'}
        </button>
      </div>

      {showOverride && (
        <div className="override-form mt-2">
          <div className="field-row">
            <div className="field">
              <ExpressionInput
                id="ov-dose"
                label="Dose"
                unit="mg"
                value={Number(overrideDose) || 0}
                onChange={(v) => setOverrideDose(String(v))}
                min={0}
                decimals={0}
                inline
              />
            </div>
            <div className="field">
              <ExpressionInput
                id="ov-interval"
                label="Interval"
                unit="hr"
                value={Number(overrideInterval) || 0}
                onChange={(v) => setOverrideInterval(String(v))}
                min={0}
                decimals={0}
                inline
              />
            </div>
          </div>
          <button
            className="btn btn-sm"
            disabled={!overrideDose || !overrideInterval}
            onClick={() => setShowReasonModal(true)}
            data-testid="apply-override"
          >
            Apply override
          </button>
        </div>
      )}

      <OverrideReasonModal
        open={showReasonModal}
        title="Override Regimen"
        onSubmit={submitOverride}
        onCancel={() => setShowReasonModal(false)}
      />
    </section>
  );
}
